import React, { useEffect, useRef, useCallback } from 'react';
import { SettingsPanel } from './SettingsPanel';
import { SimulationHUD } from './SimulationHUD';
import { SimulationHeader } from './SimulationHeader';
import { SimulationFooter } from './SimulationFooter';
import { ControlIsland } from './ControlIsland';
import { STORAGE_KEY, DEFAULT_CONFIG } from '@/constants/constants';
import { SimulationRenderer } from '@/render/SimulationRenderer';
import { safeSetItem } from '@/lib/storage';
import { StudioOnboardingModal } from '@/components/StudioOnboardingModal';
import { BugReportModal } from '@/components/BugReportModal';
import { useAuth } from '@/context/auth-context';
import { NavigationOverlay } from './NavigationOverlay';
import { UIReopenButton } from './UIReopenButton';
import { DiagnosticsOverlay } from '@/components/DiagnosticsOverlay';


// Hooks
import { useSimulationState } from '@/hooks/useSimulationState';
import { useAudioTriggers } from '@/hooks/useAudioTriggers';
import { useFileHandlers } from '@/hooks/useFileHandlers';
import { useSimulationInit } from '@/hooks/useSimulationInit';
import { useSimulationLoop } from '@/hooks/useSimulationLoop';
import { usePhysicsWorker } from '@/hooks/usePhysicsWorker';
import { useWebGL } from '@/hooks/useWebGL';
import { useKeyboardControls } from '@/hooks/useKeyboardControls';
import { useCloudSync } from '@/hooks/useCloudSync';
import { useAudioResume } from '@/hooks/useAudioResume';
import { useCollisionHandler } from '@/hooks/useCollisionHandler';
import { useStudioHeartbeat } from '@/hooks/useStudioHeartbeat';
import { useWindowAPI } from '@/hooks/useWindowAPI';
import { AUDIO } from '@/services/AudioService';
import { CloudService } from '@/services/CloudService';
import { OffscreenCanvasManager } from '@/services/OffscreenCanvasManager';
import { random } from '@/lib/random';
import { SpawnerPhysics } from '@/logic/modes/SpawnerPhysics';
import { SimulationConfig, Ball } from '@/types';

// Register all animations
import '@/logic/animations/collisions';
import '@/logic/animations/blasts';

import { audioBufferToWav } from '@/lib/audioUtils';

const CreatorStudio: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const bgCanvasRef = useRef<HTMLCanvasElement>(null);
  const webglCanvasRef = useRef<HTMLCanvasElement>(null);
  const sim2dCanvasRef = useRef<HTMLCanvasElement>(null);
  const webglRendererRef = useRef<any>(null);

  const state = useSimulationState();
  const { user } = useAuth();
  const {
    config, setConfig,
    ballCount, setBallCount,
    ballCountRef,
    bounceCount, setBounceCount,
    bounceCountRef,
    isPlaying, setIsPlaying,
    showUI, setShowUI,
    elapsedTime, setElapsedTime,
    elapsedTimeRef,
    saveStatus, setSaveStatus,
    cloudSaves, setCloudSaves,
    ballsRef,
    trailCirclesRef,
    collisionEventsRef,
    blastEventsRef,
    escapeRingsRef,
    initialEscapeRingCountRef,
    requestRef,
    lastTimeRef,
    hueRef,
    timeScaleRef,
    isRotationStoppedRef,
    shakeRef,
    counterPopRef,
    timeRemainingRef,
    currentRoundRef,
    roundStartTimeRef,
    roundOverridesRef,
    morphTimerRef,
    morphStateRef,
    escapeAccelerationBoostRef,
    containerRadiusRef,
    speedIncreaseRateRef,
    ballGrowthRateRef,
    containerShrinkRateRef,
    rhythmicBreakPiecesRef,
    persistentSpectrumBoostRef,
    persistentSpectrumTargetRef,
    rhythmicIndexRef,
    rhythmicTimeScaleRef,
    rhythmicTimeScaleRateRef,
    rhythmicResetTimeoutRef,
    rhythmicSimulationsStateRef,
    simTimeRef,
    rhythmicEscapeShieldActiveRef,
    rhythmicEscapeShieldBouncesRef,
    rhythmicEscapeShieldTimeRef,
    rhythmicEscapeShieldAngleRef,
    physicsSystemRef,
    workerRef,
    ballPoolRef,
    trailCirclePoolRef,
    collisionEventPoolRef,
    blastEventPoolRef
  } = state;

  const configRef = useRef(config);
  useEffect(() => { configRef.current = config; }, [config]);

  const hyperSpeedStateRef = useRef<{ active: boolean, startTime: number }>({ active: false, startTime: 0 });
  const zoomLevelRef = useRef<number>(1.0);

  const [isOnboardingOpen, setIsOnboardingOpen] = React.useState(false);
  const [isBugReportOpen, setIsBugReportOpen] = React.useState(false);
  const [sidebarWidth, setSidebarWidth] = React.useState(550);

  useEffect(() => {
    const shown = localStorage.getItem('studio_onboarding_shown');
    if (!shown) {
      setIsOnboardingOpen(true);
    }
  }, []);

  const handleCloseOnboarding = () => {
    setIsOnboardingOpen(false);
    localStorage.setItem('studio_onboarding_shown', 'true');
  };

  // Audio & Cloud
  useAudioResume();
  useCloudSync(setConfig, setCloudSaves);
  useStudioHeartbeat(user, configRef, isPlaying);

  // Phase 5: Deferred melody loading for performance
  useEffect(() => {
    const melodyNameOrId = config.selectedMelody;
    const customMelody = config.customMelodies.find(m => m.id === melodyNameOrId || m.name === melodyNameOrId);
    
    // If it's a cloud melody with missing data, fetch it lazily
    if (customMelody && (customMelody as any).hasCloudData && (!customMelody.frequencies || customMelody.frequencies.length === 0) && !customMelody.midiData) {
        const idToFetch = customMelody.id || customMelody.name;
        CloudService.getMelody(idToFetch).then(fullMelody => {
            if (fullMelody && fullMelody.data) {
                const melodyData = fullMelody.data;
                setConfig(prev => ({
                    ...prev,
                    customMelodies: prev.customMelodies.map(m => 
                        (m.id === idToFetch || m.name === idToFetch) 
                        ? {
                            ...m,
                            frequencies: melodyData.frequencies || (Array.isArray(melodyData) ? melodyData : []),
                            format: melodyData.format || 'FREQUENCIES',
                            midiData: melodyData.midiData,
                            hasCloudData: false
                        }
                        : m
                    )
                }));
            }
        });
    }
  }, [config.selectedMelody, config.customMelodies]);

  // Audio Triggers
  const { playCollisionSound, triggerBlast } = useAudioTriggers(
    configRef,
    escapeRingsRef,
    initialEscapeRingCountRef,
    blastEventsRef,
    blastEventPoolRef,
    morphStateRef,
    containerRadiusRef,
    shakeRef,
    isRotationStoppedRef,
    setBounceCount,
    counterPopRef
  );

  // File Handlers
  const {
    handleFileUpload,
    handleBallSoundUpload,
    isAnalyzing,
    analysisProgress,
    isUploadingBallSound
  } = useFileHandlers(config, setConfig);

  // Collision Logic
  const { onCollision } = useCollisionHandler(
      configRef,
      ballsRef,
      ballPoolRef,
      workerRef,
      hueRef,
      collisionEventsRef,
      shakeRef,
      setBallCount,
      setBounceCount
  );

  // Physics & WebGL
  const { resetWorker, sendBallsToWorker } = usePhysicsWorker(config, ballsRef, workerRef, onCollision);

  // Initialization
  const { initSimulation, getBallPreviews } = useSimulationInit(
    configRef,
    ballsRef,
    trailCirclesRef,
    collisionEventsRef,
    blastEventsRef,
    escapeRingsRef,
    initialEscapeRingCountRef,
    timeScaleRef,
    isRotationStoppedRef,
    shakeRef,
    counterPopRef,
    timeRemainingRef,
    containerRadiusRef,
    rhythmicIndexRef,
    rhythmicTimeScaleRef,
    rhythmicTimeScaleRateRef,
    morphTimerRef,
    morphStateRef,
    escapeAccelerationBoostRef,
    currentRoundRef,
    roundStartTimeRef,
    roundOverridesRef,
    ballPoolRef,
    trailCirclePoolRef,
    collisionEventPoolRef,
    blastEventPoolRef,
    speedIncreaseRateRef,
    ballGrowthRateRef,
    containerShrinkRateRef,
    rhythmicBreakPiecesRef,
    persistentSpectrumBoostRef,
    rhythmicResetTimeoutRef,
    rhythmicSimulationsStateRef,
    simTimeRef,
    rhythmicEscapeShieldActiveRef,
    rhythmicEscapeShieldBouncesRef,
    rhythmicEscapeShieldTimeRef,
    rhythmicEscapeShieldAngleRef,
    resetWorker,
    sendBallsToWorker,
    setBallCount,
    setBounceCount,
    setElapsedTime,
    setConfig,
    lastTimeRef,
    webglRendererRef
  );

  // Renderer
  const draw = useCallback(() => {
    if (!canvasRef.current) return;
    
    const previews = !isPlaying ? getBallPreviews() : undefined;

    SimulationRenderer.draw(
      canvasRef.current,
      sim2dCanvasRef.current,
      bgCanvasRef.current,
      webglRendererRef.current,
      ballsRef.current,
      trailCirclesRef.current,
      collisionEventsRef.current,
      blastEventsRef.current,
      persistentSpectrumBoostRef.current,
      escapeRingsRef.current,
      initialEscapeRingCountRef.current,
      configRef.current,
      hueRef.current,
      shakeRef.current,
      counterPopRef.current,
      timeRemainingRef.current,
      morphStateRef.current,
      containerRadiusRef.current,
      rhythmicBreakPiecesRef.current,
      zoomLevelRef.current,
      bounceCountRef.current,
      previews,
      rhythmicSimulationsStateRef.current,
      rhythmicEscapeShieldActiveRef.current,
      rhythmicEscapeShieldAngleRef.current
    );
  }, [isPlaying, getBallPreviews]);

  // Loop
  const { update } = useSimulationLoop(
    configRef,
    isPlaying,
    ballsRef,
    trailCirclesRef,
    collisionEventsRef,
    blastEventsRef,
    escapeRingsRef,
    lastTimeRef,
    hueRef,
    timeScaleRef,
    isRotationStoppedRef,
    shakeRef,
    counterPopRef,
    timeRemainingRef,
    morphTimerRef,
    morphStateRef,
    escapeAccelerationBoostRef,
    containerRadiusRef,
    rhythmicTimeScaleRef,
    rhythmicTimeScaleRateRef,
    currentRoundRef,
    roundStartTimeRef,
    roundOverridesRef,
    physicsSystemRef,
    workerRef,
    ballPoolRef,
    trailCirclePoolRef,
    collisionEventPoolRef,
    blastEventPoolRef,
    speedIncreaseRateRef,
    ballGrowthRateRef,
    containerShrinkRateRef,
    rhythmicBreakPiecesRef,
    persistentSpectrumBoostRef,
    persistentSpectrumTargetRef,
    rhythmicResetTimeoutRef,
    rhythmicIndexRef,
    rhythmicSimulationsStateRef,
    hyperSpeedStateRef,
    zoomLevelRef,
    requestRef,
    simTimeRef,
    setElapsedTime,
    setBounceCount,
    setBallCount,
    setConfig,
    setIsPlaying,
    triggerBlast,
    playCollisionSound,
    initSimulation,
    draw,
    rhythmicEscapeShieldActiveRef,
    rhythmicEscapeShieldBouncesRef,
    rhythmicEscapeShieldTimeRef,
    rhythmicEscapeShieldAngleRef,
    bounceCountRef,
    ballCountRef,
    elapsedTimeRef
  );

  useWindowAPI(update, initSimulation, setConfig, setShowUI, AUDIO, { audioBufferToWav });

  // Keyboard Controls
  const onThrow = useCallback(() => {
    const heroBall = ballsRef.current.find(b => b.id === 'hero-ball');
    if (heroBall) {
        const angleRad = (configRef.current.spaceSpiralThrowAngle || 0) * Math.PI / 180;
        const power = configRef.current.spaceSpiralThrowPower || 20;
        heroBall.vel.x = Math.cos(angleRad) * power;
        heroBall.vel.y = Math.sin(angleRad) * power;
        // Temporarily increase mass to push through boxes
        const originalMass = heroBall.mass;
        heroBall.mass = 10000;
        setTimeout(() => {
            heroBall.mass = originalMass;
        }, 1000);
    }
  }, []);

  useWebGL(webglCanvasRef, webglRendererRef);
  
  const onReset = useCallback(() => {
    // Reset rounds manually if in space spiral or other round-based modes
    initSimulation(true);
    setConfig(DEFAULT_CONFIG);
  }, [initSimulation, setConfig]);

  useKeyboardControls(config, isPlaying, setIsPlaying, initSimulation, isRotationStoppedRef, onThrow, onReset);

  useEffect(() => {
    if (bgCanvasRef.current) {
        const dpr = window.devicePixelRatio || 1;
        const w = window.innerWidth;
        const h = window.innerHeight;
        OffscreenCanvasManager.initialize(
            bgCanvasRef.current,
            configRef.current,
            w * dpr,
            h * dpr,
            hueRef.current,
            isPlaying
        );
    }
    return () => {
        OffscreenCanvasManager.terminate();
    };
  }, []);

  useEffect(() => {
    OffscreenCanvasManager.setConfig(config, hueRef.current, isPlaying);
  }, [config, isPlaying]);

  useEffect(() => {
    if (isPlaying) {
      AUDIO.resume();
      if (configRef.current.mode === 'SPACE_SPIRAL' && configRef.current.spaceSpiralSoundSpawn) {
        AUDIO.playSoundEffect(configRef.current.spaceSpiralSoundSpawn, configRef.current);
      }
    }
  }, [isPlaying]);

  const prevSelectedMelodyRef = useRef(config.selectedMelody);
  const prevModeRef = useRef(config.mode);
  const prevPlaylistEnabledRef = useRef(config.playlistEnabled);

  useEffect(() => { 
    const playlistChanged = prevPlaylistEnabledRef.current !== config.playlistEnabled;
    const melodyChanged = prevSelectedMelodyRef.current !== config.selectedMelody;
    const modeChanged = prevModeRef.current !== config.mode;
    
    prevPlaylistEnabledRef.current = config.playlistEnabled;
    prevSelectedMelodyRef.current = config.selectedMelody;
    prevModeRef.current = config.mode;

    // Skip reset if only melody or playlist toggle changed and continue simulation is enabled
    const continueSim = config.mode === 'RHYTHMIC' && config.rhythmicPlaylistContinueSimulation;
    if ((melodyChanged || playlistChanged) && !modeChanged && continueSim) {
        return;
    }

    initSimulation(true); 
  }, [
    config.mode, 
    config.initialBalls, 
    config.escapeRingCount, 
    config.containerRadius,
    config.playlistEnabled,
    config.spaceSpiralBoxCount,
    config.spaceSpiralBoxSpacing,
    config.spaceSpiralBoxWidth,
    config.spaceSpiralBoxHeight,
    config.spaceSpiralScale,
    config.spaceSpiralMinBoxScale,
    config.spaceSpiralMaxBoxScale,
    config.spaceSpiralRotation,
    config.spaceSpiralBoxRotation,
    config.spaceSpiralBoxShape,
    config.spaceSpiralRepeats,
    config.spaceSpiralBallOffsetX,
    config.spaceSpiralBallOffsetY,
    config.selectedMelody,
    config.customMelodies,
    config.rhythmicPlaylistContinueSimulation,
    initSimulation
  ]);

  useEffect(() => {
    const resize = () => { 
        const dpr = window.devicePixelRatio || 1;
        const w = window.innerWidth;
        const h = window.innerHeight;

        if (canvasRef.current) { 
            canvasRef.current.width = w * dpr; 
            canvasRef.current.height = h * dpr; 
            canvasRef.current.style.width = `${w}px`;
            canvasRef.current.style.height = `${h}px`;
        }
        if (bgCanvasRef.current) {
            bgCanvasRef.current.style.width = `${w}px`;
            bgCanvasRef.current.style.height = `${h}px`;
            if (OffscreenCanvasManager.isUsingOffscreenWorker()) {
                OffscreenCanvasManager.resize(w * dpr, h * dpr);
            } else {
                bgCanvasRef.current.width = w * dpr;
                bgCanvasRef.current.height = h * dpr;
            }
        }
        if (webglCanvasRef.current) {
            webglCanvasRef.current.width = w * dpr;
            webglCanvasRef.current.height = h * dpr;
            webglCanvasRef.current.style.width = `${w}px`;
            webglCanvasRef.current.style.height = `${h}px`;
        }
        if (sim2dCanvasRef.current) {
            sim2dCanvasRef.current.width = w * dpr;
            sim2dCanvasRef.current.height = h * dpr;
            sim2dCanvasRef.current.style.width = `${w}px`;
            sim2dCanvasRef.current.style.height = `${h}px`;
        }
        if (webglRendererRef.current) {
            webglRendererRef.current.resize(w * dpr, h * dpr);
        }
        if (workerRef.current) {
            workerRef.current.postMessage({ 
                type: 'UPDATE_DIMENSIONS', 
                payload: { width: w, height: h } 
            });
        }
    };
    window.addEventListener('resize', resize); resize(); 
    const isWorker = typeof window !== 'undefined' && window.location.search.includes('isWorker=true');
    if (!isWorker) {
        requestRef.current = requestAnimationFrame(() => update());
    }
    return () => { 
        window.removeEventListener('resize', resize); 
        cancelAnimationFrame(requestRef.current); 
    };
  }, [update, requestRef]);

  useEffect(() => {
    if (config.darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [config.darkMode]);

  return (
    <div className={`relative w-full h-screen font-sans overflow-hidden transition-colors duration-500 ${config.darkMode ? 'bg-neutral-950 text-white' : 'bg-white text-neutral-800'}`} style={{ backgroundColor: config.outerColor }}>
      <div 
        className="relative w-full h-full transition-all duration-300 ease-in-out"
        style={{ 
          marginRight: (config.pinnedSidebar && showUI) ? `${sidebarWidth}px` : '0',
          width: (config.pinnedSidebar && showUI) ? `calc(100% - ${sidebarWidth}px)` : '100%'
        }}
      >
        <NavigationOverlay config={config} setIsBugReportOpen={setIsBugReportOpen} />
        
        <canvas ref={bgCanvasRef} className="absolute inset-0 z-0 pointer-events-none" />
        <canvas ref={webglCanvasRef} className="absolute inset-0 pointer-events-none z-10" />
        <canvas ref={sim2dCanvasRef} className="absolute inset-0 pointer-events-none z-15" />
        <canvas ref={canvasRef} className="absolute inset-0 cursor-pointer z-20" onClick={() => setIsPlaying(!isPlaying)} />
        <SimulationHUD 
          elapsedTime={elapsedTime} 
          timeScale={timeScaleRef.current} 
          ballCount={ballCount}
          bounceCount={bounceCount}
          ringsLeft={escapeRingsRef.current.length}
          timeRemaining={timeRemainingRef.current}
          currentRound={currentRoundRef.current}
          containerRadius={containerRadiusRef.current}
          ballRadius={config.ballRadius}
          gravity={config.gravity}
          restitution={config.restitution}
          config={config} 
        />
        <DiagnosticsOverlay
          ballsRef={ballsRef}
          trailCirclesRef={trailCirclesRef}
          collisionEventsRef={collisionEventsRef}
          blastEventsRef={blastEventsRef}
          ballPoolRef={ballPoolRef}
          trailCirclePoolRef={trailCirclePoolRef}
          collisionEventPoolRef={collisionEventPoolRef}
          blastEventPoolRef={blastEventPoolRef}
          config={config}
        />
        {!config.renderOrder.includes('HUD') && (
          <>
            <SimulationHeader config={config} currentRgb={`hsl(${hueRef.current}, 100%, 50%)`} ballCount={ballCount} ringsLeft={escapeRingsRef.current.length} bounceCount={bounceCount} isEscape={config.mode === 'ESCAPE'} />
            <SimulationFooter config={config} currentRgb={`hsl(${hueRef.current}, 100%, 50%)`} />
          </>
        )}
        {config.showControlIsland && (
          <ControlIsland 
            isPlaying={isPlaying} 
            setIsPlaying={setIsPlaying} 
            onReset={() => initSimulation(true)} 
            config={config} 
          />
        )}
      </div>

      <SettingsPanel 
        config={config} 
        setConfig={setConfig} 
        showUI={showUI} 
        setShowUI={setShowUI}
        isPlaying={isPlaying} 
        setIsPlaying={setIsPlaying} 
        onReset={onReset} 
        onSave={async () => { await safeSetItem(STORAGE_KEY, JSON.stringify(config)); setSaveStatus('saved'); setTimeout(() => setSaveStatus('idle'), 2000); }} 
        saveStatus={saveStatus} 
        onFileUpload={handleFileUpload} 
        isAnalyzing={isAnalyzing} 
        analysisProgress={analysisProgress} 
        onBallSoundUpload={handleBallSoundUpload} 
        isUploadingBallSound={isUploadingBallSound}
        cloudSaves={cloudSaves}
        setCloudSaves={setCloudSaves}
        canvasRef={canvasRef}
        bgCanvasRef={bgCanvasRef}
        webglCanvasRef={webglCanvasRef}
        sim2dCanvasRef={sim2dCanvasRef}
        sidebarWidth={sidebarWidth}
        setSidebarWidth={setSidebarWidth}
        ballPoolRef={ballPoolRef}
        trailCirclePoolRef={trailCirclePoolRef}
        collisionEventPoolRef={collisionEventPoolRef}
        blastEventPoolRef={blastEventPoolRef}
        workerRef={workerRef}
      />

      <UIReopenButton showUI={showUI} setShowUI={setShowUI} config={config} />

      <StudioOnboardingModal isOpen={isOnboardingOpen} onClose={handleCloseOnboarding} />
      <BugReportModal isOpen={isBugReportOpen} onClose={() => setIsBugReportOpen(false)} userEmail={user?.email} />
    </div>
  );
};

export default CreatorStudio;
